import { useCallback } from 'react'

import { useUserSettings } from '#lib/user-settings/use-user-settings'

/**
 * The model the user last *started* a task or session with, remembered across
 * reloads and across workspaces so a start surface can preselect it.
 *
 * `null` means nothing has been remembered yet, which callers should resolve to
 * the platform default rather than render as an empty picker. A remembered id
 * is deliberately **not** validated against the live model catalog here: the
 * catalog is fetched asynchronously, so an id that looks unknown may simply be
 * one whose config has not arrived yet. Deciding whether a remembered id is
 * still offered belongs to the composer, which can see that loading state.
 *
 * Mirrors `useLastSelectedRepository`, deliberately including its `useCallback`
 * — but note the setter is only as stable as `useUserSettings`, which rebuilds
 * `setUserSettings` on every render. Treat this as a callback to invoke, not as
 * a dependency that will hold an effect still.
 */
export function useLastSelectedModel() {
  const [settings, setUserSettings] = useUserSettings()

  const lastModelId = settings.lastSelectedModel

  const setLastModelId = useCallback(
    (modelId: string) => {
      setUserSettings('lastSelectedModel', modelId)
    },
    [setUserSettings],
  )

  return [lastModelId, setLastModelId] as const
}
